"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-3 w-14 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-block px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-default font-medium text-foreground hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-[var(--accent-deep-red)] transition-all duration-[500ms] ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-10 w-3/4 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="group flex items-baseline gap-5 text-foreground" style={{
      animation: `fadeUp 700ms ease-out ${i * 80}ms both`,
      willChange: "opacity, transform"
    }}>
          <span className="font-mono text-xs text-[var(--accent-deep-red)] tracking-widest">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl font-light tracking-tight group-hover:text-[var(--accent-deep-red)] transition-colors duration-300">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  useEffect(() => {
    return () => {
      setMobileOpen(false);
    };
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (mobileOpen) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, [mobileOpen]);
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => document.body.style.overflow = "";
  }, [mobileOpen]);
  return <section data-section-id="4073" id="main-navigation" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 ${scrolled ? "bg-background/70 backdrop-blur-xl border-b border-border" : "bg-background"}`}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <div className="flex h-16 md:h-[72px] items-center justify-between">
          <div className="flex items-center shrink-0">
            <Link to="/" aria-label="ClearLine - Home" className="flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex flex-1 justify-center px-8">
            {!searchOpen && <MenuProvider menu_id="16" className="header-menu">
                <DesktopMenuConsumer />
              </MenuProvider>}
            {searchOpen && <div className="w-full max-w-xl">
                <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search ClearLine..." className="w-full" />
              </div>}
          </div>

          <div className="flex items-center gap-4 md:gap-6 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(s => !s)} className="hidden lg:inline-flex h-9 w-9 text-foreground hover:bg-transparent hover:text-[var(--accent-deep-red)]" aria-label={searchOpen ? "Close search" : "Open search"}>
              {searchOpen ? <X className="size-4" strokeWidth={1.25} /> : <Search className="size-4" strokeWidth={1.25} />}
            </Button>

            <span aria-hidden="true" className="hidden lg:inline font-default text-base text-foreground/30 select-none">
              |
            </span>

            <Button variant="ghost" size="icon" onClick={e => {
            e.stopPropagation();
            setMobileOpen(true);
          }} className="lg:hidden h-9 w-9 text-foreground hover:bg-transparent hover:text-[var(--accent-deep-red)]" aria-label="Open menu">
              <Menu className="size-5" strokeWidth={1.25} />
            </Button>
          </div>
        </div>
      </div>

      {mobileOpen && <div onClick={e => {
      if (e.target === e.currentTarget) {
        setMobileOpen(false);
      }
    }} className="fixed inset-0 z-[9999] transition-opacity" style={{
      backgroundColor: '#000000',
      pointerEvents: mobileOpen ? 'auto' : 'none'
    }}>
          <div className="flex h-16 items-center justify-between px-5 border-b border-border bg-background">
            <Link to="/" aria-label="ClearLine - Home" onClick={() => setMobileOpen(false)} className="flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="h-9 w-9 text-foreground hover:bg-transparent hover:text-[var(--accent-deep-red)]" aria-label="Close menu">
              <X className="size-5" strokeWidth={1.25} />
            </Button>
          </div>

          <div onClick={e => e.stopPropagation()} className="px-8 py-12 overflow-y-auto h-[calc(100vh-4rem)] bg-black relative">
            <div className="mb-10">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search ClearLine..." className="w-full" />
            </div>

            <MenuProvider menu_id="16" className="header-menu-mobile">
              <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
            </MenuProvider>

            <div className="mt-16 pt-8 border-t border-border">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                ClearLine Agency
              </p>
              <p className="mt-3 font-default text-lg text-foreground/70">
                AI Visibility Experts
              </p>
            </div>
          </div>
        </div>}
    </section>;
}